import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useTranslation } from '../i18n/useTranslation.js';

import { tenantsApi } from './platformApi.js';

/**
 * Zeigt den Fortschritt einer Einrichtung.
 *
 * Bewusst die WIRKLICHEN Schritte statt eines Balkens, der etwas behauptet.
 * Wenn ein Lauf hängt, soll ablesbar sein woran - "Migrationen finance" ist
 * eine Auskunft, "73 Prozent" ist keine.
 */

/** Ein Schritt-Schlüssel wird zu einem Satz. Unbekanntes bleibt lesbar. */
function stepLabel(key, t) {
  if (key === 'role') return t('platform.provisioning.step.role');
  if (key === 'registry') return t('platform.provisioning.step.registry');
  if (key === 'company_settings') return t('platform.provisioning.step.company');
  if (key === 'admin_user') return t('platform.provisioning.step.admin');
  if (key === 'admin_employee') return t('platform.provisioning.step.adminEmployee');
  if (key === 'smoke') return t('platform.provisioning.step.smoke');
  if (key === 'activate') return t('platform.provisioning.step.activate');
  if (key.startsWith('db:')) return t('platform.provisioning.step.db', { service: key.slice(3) });
  if (key.startsWith('migrate:')) return t('platform.provisioning.step.migrate', { service: key.slice(8) });
  if (key.startsWith('drop:')) return t('platform.provisioning.step.drop', { name: key.slice(5) });
  return key;
}

const MARK = { done: '✓', running: '·', failed: '✕', undone: '↺' };

export function ProvisioningStatusPanel({ jobId, onFinished }) {
  const { t } = useTranslation();

  const { data, error } = useQuery({
    queryKey: ['platform', 'job', jobId],
    queryFn: () => tenantsApi.job(jobId),
    enabled: Boolean(jobId),
    // Nur nachfragen, solange etwas läuft. Ein fertiger Auftrag ändert sich
    // nicht mehr, und eine Abfrage im Sekundentakt gegen einen erledigten
    // Auftrag ist reine Last.
    refetchInterval: (query) => {
      const status = query.state.data?.job?.status;
      return status && ['done', 'failed', 'rolled_back'].includes(status) ? false : 1500;
    },
  });

  const job = data?.job;
  const steps = data?.steps || [];
  const finished = job && ['done', 'failed', 'rolled_back'].includes(job.status);

  /* Die Rückmeldung gehört in einen Effekt, nicht in den Aufbau.
     Während des Aufbaus den Zustand des Elternteils zu ändern löst einen
     zweiten Durchlauf mitten im ersten aus - hier fällt das keiner
     Lint-Regel auf, weil die Konfiguration .jsx nicht prüft. */
  useEffect(() => {
    if (finished && onFinished) {
      onFinished(job);
    }
  }, [finished, job, onFinished]);

  if (error) {
    return <div className="alert danger">{error.message}</div>;
  }
  if (!job) {
    return <p className="muted text-sm">{t('platform.provisioning.starting')}</p>;
  }

  const doneCount = steps.filter((s) => s.status === 'done').length;

  return (
    <div className="provisioning-status">
      <p className="text-sm" style={{ marginTop: 0 }}>
        {job.status === 'done' && <strong>{t('platform.provisioning.done')}</strong>}
        {job.status === 'failed' && <strong>{t('platform.provisioning.failed')}</strong>}
        {job.status === 'rolled_back' && <strong>{t('platform.provisioning.rolledBack')}</strong>}
        {!finished && (
          <>
            {t('platform.provisioning.running')}{' '}
            <span className="mono">
              {doneCount}/{steps.length || '…'}
            </span>
          </>
        )}
      </p>

      {job.error && <div className="alert danger text-sm">{job.error}</div>}

      <ol className="provisioning-steps">
        {steps.map((step) => (
          <li key={step.key} data-status={step.status}>
            <span className="provisioning-mark" aria-hidden="true">
              {MARK[step.status] || '·'}
            </span>
            <span>{stepLabel(step.key, t)}</span>
            {step.resourceName && step.resourceKind === 'database' && (
              <span className="muted mono text-xs">{step.resourceName}</span>
            )}
          </li>
        ))}
      </ol>

      {job.status === 'rolled_back' && (
        <p className="muted text-sm">{t('platform.provisioning.rolledBack.hint')}</p>
      )}
    </div>
  );
}
